import { css } from '@emotion/react';
import { AppointmentTypesTypes } from '@frontend/api-appointment-types';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { theme } from '@frontend/theme';
import { currency, Text } from '@frontend/design-system';
import { CompactTable, CompactTableRowActionsProps } from '../compact-table';
import { AppointmentTypeTableProps } from './types';

type Props = {
  appointmentTypesData: AppointmentTypesTypes.AppointmentType[];
  tableRowActions: CompactTableRowActionsProps<AppointmentTypesTypes.AppointmentType>['actions'];
} & AppointmentTypeTableProps;

export const AppointmentTypeCompactTable = ({
  showAmountColumn,
  appointmentTypesData,
  isLoading = false,
  tableRowActions,
  externalAppointmentTypes,
}: Props) => {
  const { t } = useTranslation('schedule');

  return (
    <CompactTable
      tableId='appointment-type-compact-table'
      isLoading={isLoading}
      data={appointmentTypesData}
      rowElement={(appointmentType: AppointmentTypesTypes.AppointmentType) => (
        <CompactTable.RowHeader title={appointmentType.name} />
      )}
      expandedRowElement={(appointmentType: AppointmentTypesTypes.AppointmentType) => (
        <ExpandedRowElement
          appointmentType={appointmentType}
          showAmountColumn={showAmountColumn}
          externalAppointmentTypes={externalAppointmentTypes}
        />
      )}
      toolbarElement={
        <div css={styles.toolbar}>
          <CompactTable.ExpandAll />
        </div>
      }
      rowActions={{
        actions: tableRowActions,
      }}
      listHeight={400}
      {...(!isLoading && {
        emptyStateConfig: {
          type: 'schedule',

          header: '',
          description: (
            <div css={{ textAlign: 'center' }}>
              <Text color='subdued'>{t('Ready to start scheduling?')}</Text>
              <Text color='subdued'>{t('Set up your first appointment type to start taking bookings.')}</Text>
            </div>
          ),
        },
      })}
    />
  );
};

const ExpandedRowElement = ({
  appointmentType,
  showAmountColumn,
  externalAppointmentTypes,
}: {
  appointmentType: AppointmentTypesTypes.AppointmentType;
  showAmountColumn: boolean | undefined;
  externalAppointmentTypes: AppointmentTypesTypes.ExternalAppointmentType[] | undefined;
}) => {
  const { t } = useTranslation('schedule');
  const amount = appointmentType.hasGlobalBookingDeposit
    ? appointmentType.globalBookingDepositAmount
    : appointmentType.hasBookingDeposit
    ? appointmentType.bookingDepositAmount
    : 0;
  const hasExternalAppointmentTypes = !!externalAppointmentTypes?.length;

  const externalAppointmentTypesName = externalAppointmentTypes?.find(
    ({ id }) => id === appointmentType.externalAppointmentTypeId
  )?.name;

  return (
    <CompactTable.ExpandedRow>
      {hasExternalAppointmentTypes && (
        <CompactTable.InfoRow
          label={t('Imported Name')}
          value={
            externalAppointmentTypesName ? (
              externalAppointmentTypesName
            ) : (
              <>
                <Icon name='alert-invert' size={24} color='error' />
                <Text css={{ marginLeft: theme.spacing(1) }} size='small' color='light' as='span'>
                  {t('Please select external appointment type')}
                </Text>
              </>
            )
          }
        />
      )}
      <CompactTable.InfoRow label={t('Duration')} value={appointmentType.durationMinutes + t(' mins')} />
      {showAmountColumn && (
        <CompactTable.InfoRow label={t('Payment Amount')} value={amount ? currency(amount, { decimals: 2 }) : '-'} />
      )}
      <CompactTable.InfoRow
        label={t('Providers')}
        value={appointmentType.providerAssets
          ?.filter((provider) => provider.available)
          .map((provider) => provider.providerName)
          .join(', ')}
      />
    </CompactTable.ExpandedRow>
  );
};

const styles = {
  toolbar: css`
    width: 100%;
    display: flex;
    justify-content: flex-end;
  `,
};
